/**
 * Persistence - Sauvegarde et chargement complet
 * - Patterns (séquences)
 * - Paramètres des synthétiseurs (cutoff, resonance, etc.)
 * - Settings globaux (BPM)
 * - Compatibilité ascendante (anciens fichiers complétés automatiquement)
 */

const STORAGE_KEY = 'rebirth_clone_data';
const STORAGE_VERSION = '2.0';

// Pattern bank IDs
export const PATTERN_BANKS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

// ============================================
// VALEURS PAR DÉFAUT
// ============================================

/**
 * Structure par défaut des patterns
 * IMPORTANT: Ajouter ici tout nouveau pattern lors de l'ajout de modules
 */
export function getDefaultPatterns() {
    return {
        // TB-303 #1
        bass1: Array(16).fill(false),
        bass1Notes: Array(16).fill('C2'),
        bass1Accent: Array(16).fill(false),
        bass1Slide: Array(16).fill(false),

        // TB-303 #2
        bass2: Array(16).fill(false),
        bass2Notes: Array(16).fill('C3'),
        bass2Accent: Array(16).fill(false),
        bass2Slide: Array(16).fill(false),

        // TR-808
        kick: Array(16).fill(false),
        snare: Array(16).fill(false),
        hihat: Array(16).fill(false),
        openhh: Array(16).fill(false),
        clap: Array(16).fill(false),
        rimshot: Array(16).fill(false),
        lowtom: Array(16).fill(false),
        midtom: Array(16).fill(false),
        hitom: Array(16).fill(false),
        cowbell: Array(16).fill(false),

        // TR-909
        kick909: Array(16).fill(false),
        snare909: Array(16).fill(false),
        clap909: Array(16).fill(false),
        rimshot909: Array(16).fill(false),
        hh909: Array(16).fill(false),
        openhh909: Array(16).fill(false),
        ride909: Array(16).fill(false),
        crash909: Array(16).fill(false),

        // Minimoog
        minimoogNotes: Array(16).fill('C2'),
        minimoogGates: Array(16).fill(false),

        // MS-20
        ms20Notes: Array(16).fill('C2'),
        ms20Gates: Array(16).fill(false),

        // Jupiter-8
        jupiter8Notes: Array(16).fill('C3'),
        jupiter8Gates: Array(16).fill(false),

        // ARP 2600
        arp2600Notes: Array(16).fill('C3'),
        arp2600Gates: Array(16).fill(false),

        // Juno-106
        juno106Notes: Array(16).fill('C3'),
        juno106Gates: Array(16).fill(false),

        // SH-101
        sh101Notes: Array(16).fill('C2'),
        sh101Gates: Array(16).fill(false),

        // MiniMod
        minimodNotes: Array(16).fill('C3'),
        minimodGates: Array(16).fill(false)
    };
}

/**
 * Structure par défaut des paramètres de synthétiseurs
 * IMPORTANT: Ajouter ici tout nouveau paramètre lors de modifications
 * NOTE: Les sends (delaySend, reverbSend) sont maintenant dans getMixerDefaults()
 */
export function getDefaultSynthParams() {
    return {
        tb303_1: {
            cutoff: 1000,
            resonance: 15,
            envMod: 2000,
            decay: 0.2,
            accentLevel: 0.5,
            waveform: 'sawtooth',
            slideTime: 0.06,
            distortion: 0.2,
            drive: 0.3
        },
        tb303_2: {
            cutoff: 1500,
            resonance: 12,
            envMod: 2500,
            decay: 0.15,
            accentLevel: 0.5,
            waveform: 'sawtooth',
            slideTime: 0.06,
            distortion: 0.2,
            drive: 0.3
        },
        tr808: {
            // KICK
            kickLevel: 0.8,
            kickTone: 50,
            kickDecay: 0.5,
            // SNARE
            snareLevel: 0.8,
            snareTone: 0.5,
            snareSnappy: 0.5,
            // CLAP
            clapLevel: 0.7,
            clapDecay: 0.15,
            // RIMSHOT
            rimshotLevel: 0.7,
            rimshotTune: 0.5,
            // HI-HATS
            hhLevel: 0.7,
            hhDecay: 0.08,
            openHHLevel: 0.7,
            openHHDecay: 0.3,
            // TOMS
            lowTomLevel: 0.7,
            lowTomTune: 0.5,
            midTomLevel: 0.7,
            midTomTune: 0.5,
            hiTomLevel: 0.7,
            hiTomTune: 0.5,
            // COWBELL
            cowbellLevel: 0.6
        },
        tr909: {
            // KICK
            kickLevel: 0.8,
            kickAttack: 0.5,
            kickTune: 60,
            kickDecay: 0.4,
            // SNARE
            snareLevel: 0.8,
            snareTone: 0.5,
            snareTune: 0.5,
            snareSnappy: 0.6,
            // CLAP
            clapLevel: 0.7,
            clapDecay: 0.12,
            // RIMSHOT
            rimshotLevel: 0.7,
            rimshotTune: 0.5,
            // HI-HATS
            hhLevel: 0.7,
            hhDecay: 0.06,
            openHHLevel: 0.7,
            openHHDecay: 0.4,
            // CYMBALS
            rideLevel: 0.6,
            rideTune: 0.5,
            rideDecay: 1.5,
            crashLevel: 0.6,
            crashTune: 0.5,
            crashDecay: 2.5
        },
        minimoog: {
            osc1Waveform: 'sawtooth',
            osc1Octave: 0,
            osc1Tune: 0,
            osc1Level: 0.8,
            osc2Waveform: 'sawtooth',
            osc2Octave: 0,
            osc2Tune: 0,
            osc2Level: 0.6,
            osc3Waveform: 'sawtooth',
            osc3Octave: -1,
            osc3Tune: 0,
            osc3Level: 0.4,
            noiseLevel: 0,
            cutoff: 2000,
            resonance: 1,
            filterEnvAmount: 0.5,
            filterKeyTrack: 0.5,
            filterAttack: 0.01,
            filterDecay: 0.3,
            filterSustain: 0.4,
            ampAttack: 0.01,
            ampDecay: 0.2,
            ampSustain: 0.7,
            ampRelease: 0.3,
            glide: 0,
            volume: 0.7,
            seqSpeed: 1  // Speed multiplier: 0.25, 0.5, 1, 2, 4
        },
        ms20: {
            vco1Waveform: 'sawtooth',
            vco1Octave: 0,
            vco1Level: 0.8,
            vco2Waveform: 'sawtooth',
            vco2Octave: 0,
            vco2Detune: 0,
            vco2Level: 0.6,
            ringModLevel: 0,
            noiseLevel: 0,
            hpfCutoff: 20,
            hpfResonance: 0,
            lpfCutoff: 5000,
            lpfResonance: 1,
            lpfEnvAmount: 0.5,
            lpfKeyTrack: 0.5,
            eg1Attack: 0.01,
            eg1Decay: 0.3,
            eg1Sustain: 0.4,
            eg1Release: 0.3,
            eg2Attack: 0.01,
            eg2Decay: 0.2,
            eg2Sustain: 0.7,
            eg2Release: 0.3,
            mgRate: 5,
            mgPitchAmount: 0,
            mgFilterAmount: 0,
            portamento: 0,
            volume: 0.7
        },
        jupiter8: {
            vco1Wave: 'sawtooth',
            vco1Octave: 0,
            vco1Level: 0.7,
            vco1PulseWidth: 0.5,
            vco2Wave: 'sawtooth',
            vco2Octave: 0,
            vco2Level: 0.5,
            vco2Detune: 5,
            vco2PulseWidth: 0.5,
            subLevel: 0.3,
            noiseLevel: 0,
            filterCutoff: 2000,
            filterResonance: 2,
            filterEnvAmount: 0.5,
            hpfCutoff: 20,
            filterAttack: 0.01,
            filterDecay: 0.3,
            filterSustain: 0.5,
            filterRelease: 0.5,
            ampAttack: 0.01,
            ampDecay: 0.2,
            ampSustain: 0.7,
            ampRelease: 0.5,
            lfoRate: 2,
            lfoWave: 'triangle',
            lfoToPitch: 0,
            lfoToFilter: 0,
            lfoToPW: 0,
            chorusRate: 0.8,
            chorusDepth: 0.5,
            chorusMix: 0.5,
            volume: 0.7,
            portamento: 0
        },
        juno106: {
            dcoSaw: true,
            dcoPulse: false,
            dcoPulseWidth: 0.5,
            dcoOctave: 0,
            subLevel: 0.3,
            noiseLevel: 0,
            hpfFreq: 0,
            lpfCutoff: 3000,
            lpfResonance: 1,
            lpfEnvAmount: 0.5,
            lpfKeyTrack: 0.5,
            envAttack: 0.01,
            envDecay: 0.3,
            envSustain: 0.6,
            envRelease: 0.4,
            lfoRate: 1,
            lfoDelay: 0,
            lfoToPitch: 0,
            lfoToFilter: 0,
            lfoToPW: 0,
            chorusMode: 2,
            vcaMode: 'env',
            volume: 0.7
        },
        sh101: {
            vcoSaw: true,
            vcoPulse: false,
            vcoPulseWidth: 0.5,
            vcoOctave: 0,
            sub1Level: 0,
            sub2Level: 0,
            noiseLevel: 0,
            lpfCutoff: 2000,
            lpfResonance: 1,
            lpfEnvAmount: 0.5,
            lpfKeyTrack: 0.5,
            envAttack: 0.01,
            envDecay: 0.3,
            envSustain: 0.5,
            envRelease: 0.4,
            vcaMode: 'env',
            lfoRate: 5,
            lfoWave: 'triangle',
            lfoToPitch: 0,
            lfoToFilter: 0,
            lfoToPW: 0,
            portamento: 0,
            volume: 0.7
        },
        arp2600: {
            vco1Freq: 440,
            vco1Fine: 0,
            vco1Wave: 'sawtooth',
            vco1PulseWidth: 0.5,
            vco1Level: 0.8,
            vco2Freq: 440,
            vco2Fine: 0,
            vco2Wave: 'sawtooth',
            vco2PulseWidth: 0.5,
            vco2Level: 0.6,
            vco2Sync: false,
            vco3Freq: 440,
            vco3Fine: 0,
            vco3Wave: 'sawtooth',
            vco3PulseWidth: 0.5,
            vco3Level: 0.4,
            vco3LowFreq: false,
            noiseType: 'white',
            noiseLevel: 0,
            ringModLevel: 0,
            filterCutoff: 5000,
            filterResonance: 1,
            filterEnvAmount: 0.5,
            filterKeyTrack: 0.5,
            env1Attack: 0.01,
            env1Decay: 0.3,
            env1Sustain: 0.5,
            env1Release: 0.5,
            env2Attack: 0.01,
            env2Decay: 0.2,
            env2Sustain: 0.7,
            env2Release: 0.3,
            lfoRate: 5,
            lfoWave: 'triangle',
            lfoToVco: 0,
            lfoToFilter: 0,
            lfoToVca: 0,
            volume: 0.7,
            portamento: 0
        },
        theremin: {
            minFreq: 65,
            maxFreq: 1047,
            vibrato: 5,
            vibratoAmount: 0.3,
            waveform: 'sine',
            portamento: 0.05,
            volume: 0.6
        },
        minimod: {
            vcoFreq: 220,
            vcoWaveform: 'sawtooth',
            lfoRate: 2,
            lfoWaveform: 'sine',
            vcfCutoff: 2000,
            vcfResonance: 5,
            vcaLevel: 0.7,
            envAttack: 0.01,
            envDecay: 0.2,
            envSustain: 0.5,
            envRelease: 0.3
        },
        effects: {
            // Delay
            delayTime: '8n',
            delayFeedback: 0.3,
            delayMix: 0.3,
            // Reverb
            reverbDecay: 2.5,
            reverbPreDelay: 0.01,
            reverbMix: 0.3,
            // Chorus
            chorusFrequency: 1.5,
            chorusDepth: 0.7,
            chorusMix: 0.3,
            // Phaser
            phaserFrequency: 0.5,
            phaserQ: 10,
            phaserMix: 0.3,
            // Flanger
            flangerRate: 0.2,
            flangerFeedback: 0.5,
            flangerMix: 0.3,
            // Bitcrusher
            crusherBits: 8,
            crusherMix: 0.3,
            // Tremolo
            tremoloFrequency: 4,
            tremoloDepth: 0.5,
            tremoloMix: 0.3,
            // Distortion
            distortionAmount: 0.4,
            distortionMix: 0.3,
            // EQ
            eqLow: 0,
            eqMid: 0,
            eqHigh: 0,
            // Compressor
            compThreshold: -24,
            compRatio: 4,
            compAttack: 0.003,
            compRelease: 0.25
        }
    };
}

/**
 * Valeurs par défaut du mixer (volume, sends, mute par canal)
 */
export function getDefaultMixerState() {
    // Structure par défaut pour chaque canal
    const defaultChannel = {
        volume: 1,
        delaySend: 0,
        reverbSend: 0,
        chorusSend: 0,
        phaserSend: 0,
        flangerSend: 0,
        crusherSend: 0,
        tremoloSend: 0,
        distortionSend: 0,
        muted: false
    };

    return {
        tb303_1: { ...defaultChannel, delaySend: 0.2, reverbSend: 0.15 },
        tb303_2: { ...defaultChannel, delaySend: 0.2, reverbSend: 0.15 },
        tr808: { ...defaultChannel, delaySend: 0.15, reverbSend: 0.1 },
        tr909: { ...defaultChannel, delaySend: 0.1, reverbSend: 0.15 },
        minimoog: { ...defaultChannel, delaySend: 0.25, reverbSend: 0.2 },
        ms20: { ...defaultChannel, delaySend: 0.2, reverbSend: 0.25 },
        jupiter8: { ...defaultChannel, delaySend: 0.2, reverbSend: 0.2, chorusSend: 0.3 },
        theremin: { ...defaultChannel, delaySend: 0.3, reverbSend: 0.3 },
        arp2600: { ...defaultChannel, delaySend: 0.25, reverbSend: 0.2 },
        juno106: { ...defaultChannel, delaySend: 0.2, reverbSend: 0.3, chorusSend: 0.2 },
        sh101: { ...defaultChannel, delaySend: 0.25, reverbSend: 0.15 },
        minimod: { ...defaultChannel, delaySend: 0.3, reverbSend: 0.25 }
    };
}

/**
 * Settings globaux par défaut
 */
export function getDefaultSettings() {
    return {
        bpm: 120,
        currentPattern: 'A',
        // Power states for all instruments
        powerStates: {
            'tb303_1': true,
            'tb303_2': true,
            tr808: true,
            tr909: true,
            minimoog: true,
            ms20: true,
            jupiter8: true,
            arp2600: true,
            juno106: true,
            sh101: true,
            minimod: true,
            theremin: true
        }
    };
}

/**
 * Crée les banques de patterns A-H
 * Chaque banque contient une copie complète des patterns par défaut
 */
export function getDefaultPatternBanks() {
    const banks = {};
    for (const id of PATTERN_BANKS) {
        banks[id] = getDefaultPatterns();
    }
    return banks;
}

// ============================================
// FUSION AVEC VALEURS PAR DÉFAUT (compatibilité)
// ============================================

/**
 * Fusionne un pattern importé avec les valeurs par défaut
 */
function mergeSinglePattern(imported) {
    const defaults = getDefaultPatterns();
    const result = { ...defaults };

    if (!imported) return result;

    for (const key of Object.keys(imported)) {
        if (Array.isArray(imported[key]) && imported[key].length === 16) {
            result[key] = [...imported[key]];
        }
    }

    // Migration: ancien format "bass" -> nouveau "bass1"
    if (imported.bass && !imported.bass1) {
        result.bass1 = [...imported.bass];
    }
    if (imported.bassNotes && !imported.bass1Notes) {
        result.bass1Notes = [...imported.bassNotes];
    }

    return result;
}

/**
 * Fusionne les banques de patterns importées avec les valeurs par défaut
 * Gère la migration depuis l'ancien format (patterns directs) vers le nouveau (banques A-H)
 */
function mergePatternBanks(imported, version) {
    const defaults = getDefaultPatternBanks();

    if (!imported) return defaults;

    // Ancien format (version < 2.0): patterns directs -> migrer vers banque A
    if (!version || parseFloat(version) < 2.0) {
        // L'ancien format avait les patterns directement, on les met dans la banque A
        defaults['A'] = mergeSinglePattern(imported);
        return defaults;
    }

    // Nouveau format: banques A-H
    const result = { ...defaults };
    for (const bankId of PATTERN_BANKS) {
        if (imported[bankId]) {
            result[bankId] = mergeSinglePattern(imported[bankId]);
        }
    }

    return result;
}

/**
 * Fusionne les paramètres synth importés avec les valeurs par défaut
 */
function mergeSynthParams(imported) {
    const defaults = getDefaultSynthParams();
    const result = {};

    for (const synthKey of Object.keys(defaults)) {
        result[synthKey] = { ...defaults[synthKey] };

        if (imported && imported[synthKey]) {
            for (const paramKey of Object.keys(defaults[synthKey])) {
                if (imported[synthKey][paramKey] !== undefined) {
                    result[synthKey][paramKey] = imported[synthKey][paramKey];
                }
            }
        }
    }

    return result;
}

/**
 * Fusionne les settings importés avec les valeurs par défaut
 */
function mergeSettings(imported) {
    const defaults = getDefaultSettings();
    return { ...defaults, ...imported };
}

// ============================================
// LOCALSTORAGE
// ============================================

/**
 * Sauvegarde tout dans LocalStorage
 * @param {Object} patternBanks - Banques de patterns A-H
 * @param {Object} synthParams - Paramètres des synthés
 * @param {Object} settings - Settings globaux (bpm, currentPattern, songMode)
 * @param {Object} mixer - État du mixer
 * @param {Array} song - Liste des steps du song mode
 */
export function saveToLocalStorage(patternBanks, synthParams, settings, mixer, song = null, scenes = null) {
    try {
        const data = {
            version: STORAGE_VERSION,
            patternBanks,
            synths: synthParams,
            settings,
            mixer,
            song,
            scenes
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        return true;
    } catch (e) {
        console.error('Erreur sauvegarde LocalStorage:', e);
        return false;
    }
}

/**
 * Charge tout depuis LocalStorage
 * Gère la migration depuis l'ancien format (version < 2.0)
 */
export function loadFromLocalStorage() {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            const data = JSON.parse(saved);
            const version = data.version;

            // Migration: ancien format utilisait "patterns", nouveau utilise "patternBanks"
            const patternsData = data.patternBanks || data.patterns;

            return {
                patternBanks: mergePatternBanks(patternsData, version),
                synths: mergeSynthParams(data.synths),
                settings: mergeSettings(data.settings),
                mixer: data.mixer || getDefaultMixerState(),
                song: data.song || null,
                scenes: data.scenes || null
            };
        }
    } catch (e) {
        console.error('Erreur chargement LocalStorage:', e);
    }

    return {
        patternBanks: getDefaultPatternBanks(),
        synths: getDefaultSynthParams(),
        settings: getDefaultSettings(),
        mixer: getDefaultMixerState(),
        song: null,
        scenes: null
    };
}

// ============================================
// EXPORT/IMPORT JSON
// ============================================

/**
 * Exporte tout en JSON (pour téléchargement)
 */
export function exportToJSON(patternBanks, synthParams, settings, mixer = null, scenes = null, song = null, filename = 'rebirth-pattern.json') {
    const data = {
        version: STORAGE_VERSION,
        created: new Date().toISOString(),
        bpm: settings.bpm,
        currentPattern: settings.currentPattern,
        powerStates: settings.powerStates,
        panelStates: settings.panelStates,
        sceneMode: settings.sceneMode,
        songMode: settings.songMode,
        currentSceneId: settings.currentSceneId,
        patternBanks,
        synths: synthParams,
        mixer,
        scenes,
        song
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();

    URL.revokeObjectURL(url);
}

/**
 * Importe depuis un fichier JSON
 * Retourne une Promise avec { patternBanks, synths, settings, mixer, scenes, song }
 * Gère la migration depuis l'ancien format
 */
export function importFromJSON(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const data = JSON.parse(e.target.result);
                const version = data.version;

                // Support ancien format (patterns directement) et nouveau format (patternBanks)
                const patternsData = data.patternBanks || data.patterns || data;
                const synthsData = data.synths || null;
                const settingsData = {
                    bpm: data.bpm || data.settings?.bpm || 120,
                    currentPattern: data.currentPattern || data.settings?.currentPattern || 'A',
                    powerStates: data.powerStates || data.settings?.powerStates || getDefaultSettings().powerStates,
                    panelStates: data.panelStates || data.settings?.panelStates || {},
                    sceneMode: data.sceneMode || data.settings?.sceneMode || false,
                    songMode: data.songMode || data.settings?.songMode || false,
                    currentSceneId: data.currentSceneId || data.settings?.currentSceneId || null
                };

                resolve({
                    patternBanks: mergePatternBanks(patternsData, version),
                    synths: mergeSynthParams(synthsData),
                    settings: mergeSettings(settingsData),
                    mixer: data.mixer || null,
                    scenes: data.scenes || null,
                    song: data.song || null
                });
            } catch (err) {
                reject(new Error('Fichier JSON invalide'));
            }
        };

        reader.onerror = () => reject(new Error('Erreur lecture fichier'));
        reader.readAsText(file);
    });
}

/**
 * Efface toutes les données sauvegardées
 */
export function clearStorage() {
    localStorage.removeItem(STORAGE_KEY);
}
